import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Heading, Subheading } from "src/components/base/heading";
import { Navbar, NavbarItem, NavbarSection } from "src/components/base/navbar";
import { Table, TableHead, TableBody, TableRow, TableHeader, TableCell } from "src/components/base/table";
import { Badge } from "src/components/base/badge";
import { Code, Text } from "src/components/base/text";
import { Card } from "../shared"; // Angenommen aus deiner vorherigen Struktur

import { InformationCircleIcon } from "@heroicons/react/20/solid";

export function InfoNote() {
    return (
        <div className="mb-10 mt-10 rounded-lg border border-emerald-500/30 bg-emerald-50/50 p-4 dark:bg-emerald-950/10">
            <div className="flex gap-3">
                {/* Icon */}
                <InformationCircleIcon className="h-5 w-5 shrink-0 text-emerald-600 dark:text-emerald-400" />

                <div className="flex flex-col gap-2">
                    {/* Titel */}
                    <span className="text-sm font-semibold text-emerald-900 dark:text-emerald-200">Note</span>

                    {/* Content */}
                    <div className="text-sm leading-6 text-emerald-800 dark:text-emerald-300/90">
                        Only transformations for the{" "}
                        <code className="rounded bg-pink-50 px-1.5 py-0.5 font-mono text-[0.85em] font-bold text-pink-600 dark:bg-pink-950/30 dark:text-pink-400">
                            SITE_TOKEN
                        </code>{" "}
                        are supported. Any other transformations of the MSI (fields or values) are unsupported and they
                        can lead to installation failures and unexpected results during agent operation.
                        <br />
                        <br />A Site or Group token is mandatory when you install the Agent. It is unnecessary to use a
                        Site or Group token when you upgrade the Agent, and if you use one, it is ignored.
                    </div>
                </div>
            </div>
        </div>
    );
}

export function DownloadsDashboard() {
    const { t } = useTranslation();

    // State für die Haupt-Sektion
    const [section, setSection] = useState<"s1" | "remote">("s1");

    // State für die Installations-Tabs
    const [installTab, setInstallTab] = useState<"win" | "gpo" | "linux" | "mac">("win");

    return (
        <>
            <Navbar>
                <NavbarSection>
                    <NavbarItem onClick={() => setSection("s1")} current={section === "s1"} className="cursor-pointer">
                        SentinelOne
                    </NavbarItem>
                    <NavbarItem
                        onClick={() => setSection("remote")}
                        current={section === "remote"}
                        className="cursor-pointer"
                    >
                        Remote Access
                    </NavbarItem>
                </NavbarSection>
            </Navbar>

            <div className="mt-8">
                {section === "s1" ? (
                    <div className="space-y-10">
                        {/* ── Download Liste ── */}
                        <div>
                            <Heading>SentinelOne Downloads</Heading>
                            <Table className="mt-4" dense>
                                <TableHead>
                                    <TableRow>
                                        <TableHeader>Dateiname</TableHeader>
                                        <TableHeader>Architektur</TableHeader>
                                        <TableHeader>Betriebssystem</TableHeader>
                                        <TableHeader>Hochgeladen am</TableHeader>
                                        <TableHeader />
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    <TableRow>
                                        <TableCell className="font-medium">
                                            <a href={"#"} target={"_blank"}>
                                                SentinelAgent_Windows_24.1.msi
                                            </a>
                                        </TableCell>
                                        <TableCell>
                                            <Badge color="zinc">x64</Badge>
                                        </TableCell>
                                        <TableCell>Windows</TableCell>
                                        <TableCell className="text-zinc-500">14.03.2026</TableCell>
                                        <TableCell className="text-right">
                                            <button className="text-sm font-semibold text-blue-600">Download</button>
                                        </TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell className="font-medium">
                                            <a href={"#"} target={"_blank"}>
                                                SentinelAgent_Linux_v23.2.tar.gz
                                            </a>
                                        </TableCell>
                                        <TableCell>
                                            <Badge color="zinc">ARM64 / x64</Badge>
                                        </TableCell>
                                        <TableCell>Linux</TableCell>
                                        <TableCell className="text-zinc-500">10.03.2026</TableCell>
                                        <TableCell className="text-right">
                                            <button className="text-sm font-semibold text-blue-600">Download</button>
                                        </TableCell>
                                    </TableRow>
                                </TableBody>
                            </Table>
                        </div>

                        {/* ── Installations-Schritte ── */}
                        <div className="space-y-4">
                            <Subheading>Installationsanleitung</Subheading>

                            {/* Tab-Navigation */}
                            <div className="flex gap-4 border-b border-zinc-950/10 dark:border-white/10">
                                {["win", "gpo", "linux", "mac"].map((t) => (
                                    <button
                                        key={t}
                                        onClick={() => setInstallTab(t as any)}
                                        className={`pb-2 text-sm font-semibold transition-all ${
                                            installTab === t
                                                ? "border-b-2 border-blue-500 text-blue-600"
                                                : "text-zinc-500 hover:text-zinc-700"
                                        }`}
                                    >
                                        {t === "win" && "Windows"}
                                        {t === "gpo" && "Windows GPO"}
                                        {t === "linux" && "Linux"}
                                        {t === "mac" && "macOS"}
                                    </button>
                                ))}
                            </div>

                            {/* Tab-Content */}
                            <Card>
                                {installTab === "win" && (
                                    <div className="space-y-3">
                                        <Text className="font-bold">Standard Windows Installation</Text>
                                        <pre className="overflow-x-auto rounded-lg bg-zinc-950 p-4 font-mono text-xs text-zinc-100">
                                            msiexec /i SentinelAgent.msi SITE_TOKEN="YOUR_TOKEN_HERE" /quiet
                                        </pre>
                                    </div>
                                )}
                                {installTab === "gpo" && (
                                    <div className="space-y-3">
                                        <Text className="font-bold">Active Directory GPO Deployment</Text>
                                        <Text>
                                            <b>
                                                1. Starten Sie ORCA und öffnen Sie die Agent <Code>*.msi</Code>
                                            </b>
                                            <br />
                                            Wenn der Agent mittels einer Group-Policy (GPO) installiert werden soll,
                                            muss eine Konfigurationsdatei mit den eindeutigen Parametern erstellt
                                            werden. Es müssen für <Code>32 bit</Code> und <Code>64 bit</Code> jeweils
                                            eigene Agenten erstellt werden. Orca kann hier heruntergeladen werden:{" "}
                                            <a
                                                href={"https://docs.microsoft.com/en-us/windows/win32/msi/orca-exe"}
                                                target={"_blank"}
                                            >
                                                https://docs.microsoft.com/en-us/windows/win32/msi/orca-exe
                                            </a>
                                            .<br />
                                            <img src={"sentinelone/orca_open_s1.png"} />
                                        </Text>
                                        <Text>
                                            <b>2. Klicken Sie auf Transform &gt; New Transform</b>
                                        </Text>
                                        <Text>
                                            <b>
                                                3. Im geladenen <Code>SentinelInstaller</Code> Fester klicken Sie auf{" "}
                                                <Code>Tables</Code> &gt; <Code>Property</Code>. Führen Sie im anderen
                                                Panel einen Rechtsklick aus und wählen Sie <Code>Add Row</Code>.
                                            </b>
                                            <img src={"sentinelone/orca_sentinel_installer.png"} />
                                        </Text>
                                        <Text>
                                            <b>
                                                4. Als <Code>property name</Code> wählen Sie <Code>SITE_TOKEN</Code> und
                                                für den Wert wählen Sie den entsprechenden Site-Token aus.
                                            </b>
                                            <InfoNote />
                                            <img src={"sentinelone/orca_add_token.png"} />
                                        </Text>
                                        <Text>
                                            <b>
                                                5. Klicken Sie auf <Code>Transform</Code> &gt;{" "}
                                                <Code>Generate Transform</Code>.
                                            </b>
                                            <img src={"sentinelone/orca_generate_token.png"} />
                                        </Text>
                                        <Text>
                                            <b>
                                                6. Speicher Sie die <Code>*.MST</Code>-Datei zusammen mit der{" "}
                                                <Code>*.MSI</Code>-Datei an einem Ort an dem alle Endpunkte Zugriff
                                                haben.
                                            </b>
                                        </Text>
                                        <Text>
                                            <b>
                                                7. In der Ziel-GPO gehen Sie unter <Code>Computer Configuration</Code>{" "}
                                                &gt; <Code>Policies</Code> &gt; <Code>Software Settings</Code>. Führen
                                                Sie einen Rechtsklick auf <Code>Software Settings</Code> aus und wählen
                                                Sie <Code>New</Code> &gt; <Code>Package</Code>.
                                                <img src={"sentinelone/gpo_new_package.png"} />
                                            </b>
                                        </Text>
                                        <Text>
                                            <b>
                                                8. In dem sich jetzt öffnenden Fenster, wählen Sie die SentinelOne MSI.
                                                Im nachfolgendem <Code>Deploy Software</Code> Fenster stellen Sie
                                                sicher, dass <Code>Advanced</Code> ausgewählt ist.
                                            </b>
                                        </Text>
                                        <Text>
                                            <b>
                                                9. Nach dem Erstellen gehen Sie in die Einstellungen der Software und
                                                unter <Code>Modifications</Code> fügen Sie die eben erstellte{" "}
                                                <Code>*.MST</Code>-Datei hinzu.
                                            </b>
                                        </Text>
                                    </div>
                                )}
                                {installTab === "linux" && (
                                    <div className="space-y-3">
                                        <Text className="font-bold">Linux Agent (Ubuntu/Debian)</Text>
                                        <pre className="rounded-lg bg-zinc-950 p-4 font-mono text-xs text-zinc-100">
                                            sudo dpkg -i SentinelAgent.deb{"\n"}
                                            sudo /opt/sentinelone/bin/sentinelctl management token set "TOKEN"{"\n"}
                                            sudo /opt/sentinelone/bin/sentinelctl control start
                                        </pre>
                                        <p>&nbsp;</p>
                                        <Text className="font-bold">Linux Agent (Redhat)</Text>
                                        <pre className="rounded-lg bg-zinc-950 p-4 font-mono text-xs text-zinc-100">
                                            sudo rpm -i SentinelAgent.rpm{"\n"}
                                            sudo /opt/sentinelone/bin/sentinelctl management token set "TOKEN"{"\n"}
                                            sudo /opt/sentinelone/bin/sentinelctl control start
                                        </pre>
                                    </div>
                                )}
                                {installTab === "mac" && (
                                    <div className="space-y-3">
                                        <Text className="font-bold">macOS Installation</Text>
                                        <Text>
                                            Öffnen Sie die .pkg Datei und folgen Sie dem Installer. Geben Sie den Site
                                            Token ein, wenn Sie dazu aufgefordert werden. TODO many screenthots
                                            missing...
                                        </Text>
                                    </div>
                                )}
                            </Card>
                        </div>
                    </div>
                ) : (
                    <div>
                        <Heading>Remote Access Downloads</Heading>
                        <Text className="mt-4 italic text-zinc-500">
                            Hier stehen die Inhalte für den Fernzugriff... (Rustdesk Download für Incident-Response)
                        </Text>
                    </div>
                )}
            </div>
        </>
    );
}
